import {
    TabInnerContentContainer,
    ImportFileLink,
    BooleanSearchContainer,
    BooleanSearchWrapStyled,
    InlineInputSection,
    SpanInputMeasure,
    TabMessageContainer,
    TabUrlListContainer,
    HiddenPIIUrlsTextContainer,
} from "./segmentRulesStyles";
import { Button } from "@similarweb/ui-components";
import { DotsLoader } from "@similarweb/ui-components";
import { SWReactIcons } from "@similarweb/icons";
import { BooleanSearchInputWrap, BooleanSearchInput } from "@similarweb/ui-components";
import { ImportCSVModal } from "pro-features/components/Rule/src/RuleModes/EditRule/EditRuleModals/ImportCSVModal";
import type { FC } from "react";
import { useRef, useMemo, useCallback, useEffect, useState, useLayoutEffect } from "react";
import { i18nFilter } from "app/filters/i18nFilter";
import rulesQueryHandler from "pro-features/components/RulesQueryBuilder/src/handlers/rulesQueryHandler";
import { SegmentUrlList } from "app/pages/segments/redesigned-wizard/segment-wizard/segments-url-list/SegmentUrlList";
import type {
    ICustomStringsFilter,
    ISegmentUrl,
    IRule,
} from "app/pages/segments/redesigned-wizard/segment-wizard/types";
import { RuleTypes } from "app/pages/segments/redesigned-wizard/segment-wizard/types";
import { addItems, removeItems } from "app/pages/segments/redesigned-wizard/segment-wizard/segmentWizardHelpers";
import { FooterButton } from "app/pages/segments/redesigned-wizard/segment-wizard/segment-rules-section/segmentRulesStyles";
import { useSegmentWizardContext } from "app/pages/segments/redesigned-wizard/segment-wizard/SegmentWizardContextProvider";
import { RuleSelectedValuesRow } from "app/pages/segments/redesigned-wizard/segment-wizard/segment-rules-section/RuleSelectedValuesRow";
import { allTrackers } from "app/services/track/track";

const UPDATE_PREVIEW_DEBOUNCE = 600;

export const CustomStringFilter: FC<ICustomStringsFilter> = ({
    ruleIndex,
    onApply,
    rule,
    selectedSite,
    getUrlsPreview,
    hideUrlsPreview = false,
    hasExistingUrlsCompoundStringNoRobots,
    filterType,
    selectedTab,
}) => {
    const { segmentRules } = useSegmentWizardContext();
    const [currentRule, setCurrentRule] = useState<IRule>(null);
    const [customStringValue, setCustomStringValue] = useState("");
    const [customStringInput, setCustomStringInput] = useState(customStringValue);
    const [urlsPreview, setUrlsPreview] = useState<ISegmentUrl[]>(undefined);
    const baseUrlsPreviewRef = useRef<ISegmentUrl[]>(undefined);
    const [urlsPreviewInProgress, setUrlsPreviewInProgress] = useState<boolean>(true); // automatically start getting of urls preview
    const [hasExistingUrlsCompundStringNoRobotsRes, setHasExistingUrlsCompundStringNoRobots] = useState<boolean>(false);
    const [applyWhenReady, setApplyWhenReady] = useState(false);
    const [isOpenImportModal, setIsOpenImportModal] = useState(false);
    const inputRef = useRef(null);
    const spanInputMeasureRef = useRef(null);
    const buttonsSectionRef = useRef(null);

    useEffect(() => {
        const componentRule = { ...rule };
        // Spread is a shallow copy, and I want to fully copy only the words,
        // as the rest of the rule filters will remain unchanged while editing the current rule
        componentRule.exact = [...rule.exact];
        setCurrentRule(componentRule);
    }, []);

    const hasUserInput = useMemo(() => {
        return customStringValue && customStringValue.trim().length > 0;
    }, [customStringValue]);

    const hasUrlsForInput = useMemo(() => {
        //first case is unique for sites with no robots txt file
        return hideUrlsPreview && !urlsPreviewInProgress
            ? hasExistingUrlsCompundStringNoRobotsRes
            : urlsPreview && urlsPreview.length > 0;
    }, [urlsPreview, hasExistingUrlsCompundStringNoRobotsRes, urlsPreviewInProgress, hideUrlsPreview]);

    const onAddExact = useCallback(
        (exact: string[]) => {
            const updatedRule = addItems({ exact }, currentRule);
            setCurrentRule(updatedRule);
        },
        [currentRule],
    );

    const onApplyFilter = useCallback(
        () => onApply(currentRule, filterType, selectedTab),
        [onApply, currentRule, filterType, selectedTab],
    );
    /**
     * Updates the URLs shown in the modal using the existing user-defined rules, and the custom string user input.
     * this method gets called via the useEffect hook above.
     */
    const updateUrlsPreview = async () => {
        setUrlsPreviewInProgress(true);

        // handle all index cases that the user can edit previous Rules from include/exclude.
        // for exclude case we decrease by 1,
        // because the length of all includeRules increase by 1 because the last rule is empty rule.
        const currentIdx =
            rule.type === RuleTypes.include
                ? ruleIndex
                : segmentRules.findIndex((rule) => rule.type === RuleTypes.exclude) + ruleIndex - 1;

        const rulesForUrlsPreview = segmentRules.slice(0, currentIdx + 1);
        let isBaseRulesForUrlsPreview = true;
        // in case the user has defined a custom string, we should add it as a new rule, so that we could filter
        // all URLS according to all previously defined rules, INCLUDING the newly defined custom string
        if (customStringValue) {
            const customStringRule = rulesQueryHandler.createNewRule(RuleTypes.include, [], [customStringValue]);

            rulesForUrlsPreview.push(customStringRule);
            isBaseRulesForUrlsPreview = false;
        }
        if (hideUrlsPreview) {
            let hasExistingUrlsCompundStringNoRobotRes;
            try {
                hasExistingUrlsCompundStringNoRobotRes =
                    await hasExistingUrlsCompoundStringNoRobots(rulesForUrlsPreview);
            } catch (e) {
                hasExistingUrlsCompundStringNoRobotRes = false;
            }
            setHasExistingUrlsCompundStringNoRobots(hasExistingUrlsCompundStringNoRobotRes);
        }
        let updatedUrls;
        if (!isBaseRulesForUrlsPreview || !baseUrlsPreviewRef.current) {
            updatedUrls = await getUrlsPreview(rulesForUrlsPreview);
            if (isBaseRulesForUrlsPreview) {
                baseUrlsPreviewRef.current = updatedUrls;
            }
        }
        if (isBaseRulesForUrlsPreview) {
            updatedUrls = baseUrlsPreviewRef.current;
        }
        setUrlsPreview(updatedUrls);
        setUrlsPreviewInProgress(false);
    };

    const onRemoveCustomStrings = useCallback(
        (exact: string[]) => {
            const updatedRule = removeItems("exact", exact, currentRule);
            setCurrentRule(updatedRule);
        },
        [currentRule],
    );

    /**
     * Updates the user input.
     * This will trigger an update of the urls preview, by useEffect on the customStringValue.
     */
    const changeCustomStringTimeoutRef = useRef(null);
    const changeCustomStringValue = useCallback(
        (newCustomStringValue, immediate = false) => {
            newCustomStringValue = decodeURIComponent(newCustomStringValue)
                ?.replace("http://www.", "")
                ?.replace("https://www.", "")
                ?.replace("http://", "")
                ?.replace("https://", "")
                ?.trim();
            const updateCustomStringValue = () => {
                setCustomStringValue(newCustomStringValue);
            };

            setCustomStringInput(newCustomStringValue);
            if (newCustomStringValue === customStringValue) {
                setUrlsPreviewInProgress(false); // stop loading indicator before updating
            }

            clearTimeout(changeCustomStringTimeoutRef.current);
            if (immediate || newCustomStringValue === customStringValue) {
                updateCustomStringValue();
            } else {
                setUrlsPreviewInProgress(true); // start loading indicator when starting the debounce
                changeCustomStringTimeoutRef.current = setTimeout(updateCustomStringValue, UPDATE_PREVIEW_DEBOUNCE);
            }
        },
        [setCustomStringInput, setUrlsPreviewInProgress, customStringValue],
    );

    const handleCustomStringInput = useCallback(
        (evt) => {
            changeCustomStringValue(evt.target.value);
        },
        [changeCustomStringValue],
    );

    const handleCustomStringKeyUp = useCallback(
        (evt) => {
            switch (evt.key) {
                case "Enter":
                    setApplyWhenReady(true);
                    changeCustomStringValue(evt.target.value, true);
                    break;
                case "Escape":
                    changeCustomStringValue("", true);
                    break;
                default:
                    break;
            }
        },
        [changeCustomStringValue, setApplyWhenReady],
    );

    const handleApply = useCallback(async () => {
        // prevent adding a word when in progress or no urls
        if (urlsPreviewInProgress || !hasUserInput) {
            return;
        }

        onAddExact([customStringValue]);

        // Reset user input to the initial state
        changeCustomStringValue("", true);
    }, [hasUrlsForInput, urlsPreviewInProgress, customStringValue, onAddExact, changeCustomStringValue]);

    const openImportFileModal = useCallback(() => {
        allTrackers.dispatchTrackEvent("Button", "click", "segments/wizard/new/open/upload/file/modal");
        setIsOpenImportModal(true);
    }, []);

    const closeImportFileModal = useCallback(() => {
        allTrackers.dispatchTrackEvent("Button", "click", "segments/wizard/new/close/upload/file/modal");
        setIsOpenImportModal(false);
    }, []);

    const handleImportStrings = useCallback(
        (strings) => {
            allTrackers.dispatchTrackEvent("Button", "click", "segments/wizard/new/upload/file/modal/importStrings");
            onAddExact(strings);
        },
        [onAddExact],
    );
    const handleDownloadSampleCsv = useCallback((event: any) => {
        allTrackers.dispatchTrackEvent("Button", "click", "segments/wizard/new/upload/modal/download/csv/sample");
    }, []);

    // user input has changed, we want to update all URLS shown in the preview.
    useEffect(() => {
        updateUrlsPreview().then();
    }, [customStringValue]);

    // Note: apply the current custom string when flag is true and urls preview is ready
    useEffect(() => {
        if (applyWhenReady && !urlsPreviewInProgress) {
            setApplyWhenReady(false);
            handleApply();
        }
    }, [applyWhenReady, urlsPreviewInProgress]);

    // calculate input text width to place the ADD button just afterwards.
    useLayoutEffect(() => {
        spanInputMeasureRef.current.textContent = customStringInput;
        const textContentWidth = spanInputMeasureRef.current.offsetWidth;
        const posLeft = customStringInput
            ? Math.min(textContentWidth, inputRef.current.offsetWidth) // place after text or max at the end of input
            : inputRef.current.offsetWidth; // if empty then place at the end of input
        buttonsSectionRef.current.style.left = `${posLeft}px`;
        buttonsSectionRef.current.style.paddingRight = `${inputRef.current.offsetWidth - posLeft}px`; // pad right to the button to avoid input click
    }, [customStringInput]);

    const renderUrlList = () => {
        const exactPhrasesToHighlight = customStringValue ? [customStringValue] : [];

        return (
            <SegmentUrlList
                selectedSite={selectedSite ?? ""}
                urls={urlsPreview}
                wordHightlights={[]}
                exactPhraseHighlights={exactPhrasesToHighlight}
                shouldShowFilter={false}
                isFilterComponent={true}
            />
        );
    };

    const renderNoUrlsMsg = () => {
        return (
            <TabMessageContainer>
                <p>{i18nFilter()("segmentWizard.editRule.custom.string.modal.no.results.part1")}</p>
                <p>{i18nFilter()("segmentWizard.editRule.custom.string.modal.no.results.part2")}</p>
            </TabMessageContainer>
        );
    };

    return (
        <TabInnerContentContainer>
            <ImportCSVModal
                isOpen={isOpenImportModal}
                onClose={closeImportFileModal}
                onImportSuccess={handleImportStrings}
                onDownloadSampleCsvClick={handleDownloadSampleCsv}
                modalTitle={"segmentWizard.csvModal.title.importCustomStrings"}
            />
            {currentRule?.exact?.length > 0 && (
                <RuleSelectedValuesRow
                    isFilterMode={true}
                    title={i18nFilter()("segmentWizard.editRule.string.row.title")}
                    valuesList={currentRule.exact}
                    onRemoveValues={onRemoveCustomStrings}
                />
            )}
            <BooleanSearchContainer>
                <BooleanSearchWrapStyled showBorderBottom={hideUrlsPreview}>
                    <SWReactIcons
                        size="sm"
                        iconName="add"
                    />
                    <BooleanSearchInputWrap>
                        <BooleanSearchInput
                            ref={inputRef}
                            placeholder={i18nFilter()("segmentWizard.redesigned.editRule.custom.string.modal.input")}
                            value={customStringInput}
                            onChange={handleCustomStringInput}
                            onKeyUp={handleCustomStringKeyUp}
                            autoFocus={true}
                        />
                        <SpanInputMeasure ref={spanInputMeasureRef} />
                        <InlineInputSection ref={buttonsSectionRef}>
                            {customStringInput && (
                                <Button
                                    className="inlineButton"
                                    type="outlined"
                                    onClick={handleApply}
                                    isLoading={urlsPreviewInProgress}
                                    isDisabled={!hasUserInput || urlsPreviewInProgress}
                                >
                                    {i18nFilter()("segmentWizard.editRule.custom.string.modal.button")}
                                </Button>
                            )}
                            {urlsPreviewInProgress && <DotsLoader />}
                        </InlineInputSection>
                    </BooleanSearchInputWrap>
                    <ImportFileLink
                        onClick={openImportFileModal}
                        type="outlined"
                        iconName="download"
                    >
                        {i18nFilter()("segmentWizard.redesigned.editRule.strings.modal.button.importStrings")}
                    </ImportFileLink>
                </BooleanSearchWrapStyled>
            </BooleanSearchContainer>
            {!hideUrlsPreview && (
                <TabUrlListContainer>
                    {hasUrlsForInput ? renderUrlList() : urlsPreviewInProgress ? null : renderNoUrlsMsg()}
                </TabUrlListContainer>
            )}
            {hideUrlsPreview && (
                <HiddenPIIUrlsTextContainer>
                    {i18nFilter()("segmentWizard.redesigned.editRule.custom.strings.hide.urls.text")}
                </HiddenPIIUrlsTextContainer>
            )}
            <FooterButton
                onClick={onApplyFilter}
                isEnabled={currentRule?.exact?.length > 0}
            >
                {i18nFilter()("common.apply")}
            </FooterButton>
        </TabInnerContentContainer>
    );
};
